import { cn, withUrl } from "@/lib/utils";
import PrimaryButton from "@/components/ui/primary-button";
import Link from "next/link";
import SecondaryButton from "@/components/ui/secondary-button";
import LINKS from "@/lib/links";
import HeroImage from "@/assets/images/home/hero-image.jpeg";
import Image from "next/image";

type Cta = {
  href: string;
  text: string;
  ariaLabel: string;
};

export interface HeroProps {
  id?: string;
  className?: string;
  heading?: React.ReactNode | string;
  subheading?: React.ReactNode | string;
  image?: {
    src?: string;
    alt?: string;
  };
  primaryCta?: Cta;
  secondaryCta?: Cta;
}

const defaultProps: HeroProps = {
  heading: "Your Partner in Exceptional Senior Care",
  subheading:
    "Personalized care plans and expert staffing solutions for senior citizens in homes and facilities.",
  primaryCta: {
    href: withUrl(LINKS.careAssessment.href),
    text: "Get Started",
    ariaLabel: "Go to Care Assessment",
  },
  secondaryCta: {
    href: withUrl(LINKS.inquiryForm.href),
    text: "Inquire Now",
    ariaLabel: "Go to Inquiry Form",
  },
};

const Hero: React.FC<HeroProps> = ({
  id = "hero",
  className,
  heading,
  subheading,
  image,
  primaryCta,
  secondaryCta,
}) => {
  heading = heading ?? defaultProps.heading;
  subheading = subheading ?? defaultProps.subheading;
  primaryCta = primaryCta ?? defaultProps.primaryCta;
  secondaryCta = secondaryCta ?? defaultProps.secondaryCta;
  image = image ?? {
    alt: "A woman with glasses and a black blazer smiles and shakes hands with another person.",
  };

  return (
    <section
      id={id}
      aria-label={id}
      className={cn(
        "mx-auto flex w-full max-w-lg flex-col gap-4 p-4 md:flex-row md:gap-6 md:px-6 md:py-8 lg:gap-8 lg:px-3 lg:py-10",
        className
      )}
    >
      <div className="space-y-4 md:flex-1 lg:flex-initial lg:basis-[662px] lg:space-y-6">
        <div className="space-y-2">
          <h1 className="text-center font-montserrat text-heading-3 text-secondary-500 md:text-left md:text-heading-2 lg:text-heading-1">
            {heading}
          </h1>
          <p className="md:subheading-2 text-center font-montserrat text-subheading-3-400 text-neutral-500 md:text-left lg:text-subheading-1">
            {subheading}
          </p>
        </div>
        <div className="mx-auto flex w-fit justify-center gap-5 md:mx-0">
          {primaryCta?.text && primaryCta?.href && (
            <PrimaryButton
              id="primary-cta-home-hero"
              className="flex items-center"
              color="orange"
              size="md"
              asChild
            >
              <Link
                href={primaryCta.href}
                prefetch
                aria-label={primaryCta.ariaLabel}
              >
                {primaryCta.text}
              </Link>
            </PrimaryButton>
          )}
          {secondaryCta?.text && primaryCta?.href && (
            <SecondaryButton
              id="secondary-cta-home-hero"
              className=""
              color="teal"
              size="md"
              asChild
            >
              <Link
                href={secondaryCta.href}
                prefetch
                aria-label={secondaryCta.ariaLabel}
              >
                {secondaryCta.text}
              </Link>
            </SecondaryButton>
          )}
        </div>
      </div>
      <div
        className={cn(
          "relative h-full overflow-clip rounded-2xl shadow-[4px_4px_0px_0px_#FFD5BF] md:shadow-[6px_6px_0px_0px_#FFD5BF] lg:shadow-[8px_8px_0px_0px_#FFD5BF]",
          "mx-auto min-h-[160px] w-full flex-1 bg-center md:min-h-[312px] lg:min-h-[424px]"
        )}
      >
        {image && (
          <Image
            className="object-cover"
            src={image?.src || HeroImage}
            alt={image?.alt || "Hero Image"}
            fill
            priority
          />
        )}
      </div>
    </section>
  );
};

export default Hero;
